import React, {Suspense, lazy} from 'react';
import {Route, Routes} from 'react-router-dom';
import BackTop from 'antd/lib/back-top';
import {connect} from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {compose} from 'redux';
import getAxiosInstance, {BASE_URL_TYPE} from '../api/axios';

import LeftMenu from './LeftMenu';
import Header from './Header';
import Spinner from './Elements/Spinner';
import {updateOlarkMobileVisibility} from '../helpers/olarkLoader';
import Page404 from './Elements/Page404';
import ErrorPage from './Elements/ErrorPage';
import * as actionCreators from '../store/actions';
import CRMRoutes from './Containers/CRM/routes';
import {sitemapLink} from './Containers/Sitemap';
import {browseLink} from './Containers/Browse';
import jsTimer from '../helpers/timer';
import {getQueryParam} from '../helpers/common';
import StripeCheckoutCompleteHandler from './Elements/Stripe/StripeCheckoutCompleteHandler';
import {withRouter} from '../helpers/routerCompat';

jsTimer.setEnabled(getQueryParam('timerEnabled'));
jsTimer.start();
const axios = getAxiosInstance();
const propTypes = {
  isMobile: PropTypes.bool.isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  updateUserData: PropTypes.func.isRequired,
  fetchUserData: PropTypes.func.isRequired,
  setSubscriptionsSettings: PropTypes.func.isRequired,
  setMobile: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  userDataFetched: PropTypes.bool.isRequired,
  userData: PropTypes.instanceOf(Object).isRequired,
};

const Map = lazy(() => import(/* webpackChunkName: "Map" */ './Containers/Map/Map'));
const Sitemap = lazy(() => import(/* webpackChunkName: "Sitemap" */ './Containers/Sitemap/Sitemap'));
const Browse = lazy(() => import(/* webpackChunkName: "Browse" */ './Containers/Browse/Browse'));
const HomepageV2 = lazy(() => import(/* webpackChunkName: "HomepageV2" */ './Containers/Homepage/HomepageV2'));
const DownloadProgress = lazy(() =>
  import(/* webpackChunkName: "DownloadProgress" */ './Elements/Download/DownloadProgress'));

let previousPosition = 0;
let timer = null;

class App extends React.Component {
  static checkScroll(e) {
    const currentPosition = e.target.scrollTop;
    const upBtn = document.getElementsByClassName('ant-back-top');
    if (!_.isEmpty(upBtn[0])) {
      if (previousPosition > currentPosition) {
        upBtn[0].classList.add('show');
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          if (!_.isEmpty(upBtn[0])) {
            upBtn[0].classList.remove('show');
          }
        }, 2500);
      }
      else {
        upBtn[0].classList.remove('show');
        if (timer) {
          clearTimeout(timer);
        }
      }
      previousPosition = currentPosition;
    }
  }

  static addExceptionRecord(message) {
    axios.post(`${BASE_URL_TYPE.PROPZONE}/dblog/add`, {message});
  }

  static syncBrowseBodyClass(pathname) {
    if (pathname.startsWith(browseLink)) {
      document.body.classList.add('browse-active');
    }
    else {
      document.body.classList.remove('browse-active');
    }
  }

  constructor(props) {
    super(props);

    this.state = {
      hasError: false,
    };

    this.checkIsMobile = this.checkIsMobile.bind(this);
    document.body.classList.add('sear-app');
    const userData = _.get(window, 'drupalSettings.currentUser');
    const subscriptionsAccess = _.get(window, 'drupalSettings.subscriptionsAccess');
    if (subscriptionsAccess) {
      props.setSubscriptionsSettings(subscriptionsAccess);
    }
    if (userData) {
      props.updateUserData(userData);
    }
    else {
      props.fetchUserData();
    }
  }

  componentDidMount() {
    const {isMobile, history} = this.props;
    App.syncBrowseBodyClass(history.location.pathname);
    this.checkIsMobile();
    window.addEventListener('resize', _.debounce(this.checkIsMobile, 300));
    updateOlarkMobileVisibility(isMobile);
  }

  componentDidUpdate(prevProps) {
    const {isMobile, history} = this.props;
    if (prevProps.history.location.pathname !== history.location.pathname) {
      App.syncBrowseBodyClass(history.location.pathname);
    }
    if (prevProps.isMobile !== isMobile) {
      updateOlarkMobileVisibility(isMobile);
    }
  }

  componentWillUnmount() {
    this.checkIsMobile();
    window.removeEventListener('resize', _.debounce(this.checkIsMobile, 300));
  }

  componentDidCatch(error, info) {
    this.setState({hasError: true});
    App.addExceptionRecord(JSON.stringify({error: error.toString(), info}));
  }

  checkIsMobile() {
    const {setMobile, isMobile} = this.props;
    const w = window.innerWidth || _.get(document, 'documentElement.clientWidth') || document.body.clientWidth;
    if (w < 768 && !isMobile) {
      setMobile(true);
      document.body.classList.add('is-mobile');
    }
    else if (w >= 768 && isMobile) {
      setMobile(false);
      document.body.classList.remove('is-mobile');
    }
  }

  // eslint-disable-next-line class-methods-use-this
  closePropertyPopup(e) {
    if (!e.target.closest('.map-wrapper')) {
      const shapePopup = document.querySelector('.shape-popup');
      if (shapePopup) {
        shapePopup.remove();
      }
    }
  }

  render() {
    // eslint-disable-next-line react/prop-types
    const {downloadParams} = this.props;
    const {hasError} = this.state;
    // Display fallback UI on app crash.
    if (hasError) {
      return <ErrorPage />;
    }
    const {
      loading, userDataFetched, userData, history,
    } = this.props;
    const path = history.location.pathname;
    const isBrowsePath = path.startsWith(browseLink);
    const routes = (
      <Suspense fallback={<Spinner />}>
        <Routes>
          {/* Browse before Map /city/us routes — /browse/us/fl must not hit Map */}
          <Route path={`${browseLink}/us/*`} element={<Browse />} />
          <Route path={`${browseLink}/*`} element={<Browse />} />
          <Route
            path="/zoning-detail/:propertyAlias"
            element={<Map closePropertyPopup={this.closePropertyPopup} />}
          />
          <Route
            path="/:routeType/us/*"
            element={<Map closePropertyPopup={this.closePropertyPopup} />}
          />
          <Route path="/" element={<HomepageV2 />} />
          {CRMRoutes}
          <Route path={`${sitemapLink}/*`} element={<Sitemap />} />
          <Route path="*" element={<Page404 />} />
        </Routes>
      </Suspense>
    );

    return (
      <React.Fragment>
        <div role="presentation" className="ch" onClick={this.closePropertyPopup} onKeyDown={this.closePropertyPopup}>
          <Header urlPath={history.location.pathname} />
          {window.location.pathname !== '/hmp2'
            && window.location.pathname !== '/'
            && window.location.pathname !== sitemapLink
            && !window.location.pathname.startsWith(browseLink) && <LeftMenu />}
          {/* Spinner for loading state */}
          {(loading || !userDataFetched) && !isBrowsePath && <Spinner />}
          {/* Always mounted: DownloadProgress keeps its own list of in-flight
              downloads and clears the redux trigger after consuming it, so
              gating its mount on `downloadParams` would unmount it (and drop
              the list) the moment it resets the trigger. It renders nothing
              while there are no active downloads. */}
          <Suspense fallback={null}>
            <DownloadProgress downloadParams={downloadParams} userData={userData} />
          </Suspense>
          {/* Registered routes */}
          <div className="main" id="main" onScroll={App.checkScroll}>
            {!userDataFetched && isBrowsePath && <Spinner />}
            {userDataFetched && routes}
          </div>
          {/* Popup container element */}
          <div id="popup-container" />
          <StripeCheckoutCompleteHandler />
        </div>
        {/* Back to top button for mobile */}
        <BackTop target={() => document.getElementById('main')} visibilityHeight={1000}>
          <i className="icon-b icon-b-arrow-circle-up" />
        </BackTop>
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => ({
  pageData: state.doc.pageData,
  loading: state.ui.loading,
  downloadParams: state.ui.downloadParams,
  isMobile: state.ui.isMobile,
  userDataFetched: state.user.userDataFetched,
  userData: state.user.userData,
});

const mapDispatchToProps = dispatch => ({
  setLoading: state => dispatch(actionCreators.setLoading(state)),
  setMobile: state => dispatch(actionCreators.setMobile(state)),
  setPageData: state => dispatch(actionCreators.setPageData(state)),
  fetchUserData: () => dispatch(actionCreators.fetchUserData()),
  updateUserData: data => dispatch(actionCreators.updateUserData(data)),
  setSubscriptionsSettings: data => dispatch(actionCreators.setSubscriptionsSettings(data)),
});

App.propTypes = propTypes;

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(App);
